import { useCallback, useLayoutEffect, useRef } from 'react';

// A list whose rows MOVE smoothly instead of jumping - "FLIP" (First, Last, Invert, Play) on the browser's own Web
// Animations API, so no animation library is needed.
//
//   const flip = useFlipList(orderSignature, { selector: '[data-row-key]', attr: 'rowKey', liftKey });
//   <div ref={flip.containerRef}> {rows.map((r) => <div data-row-key={r.key} ... />)} </div>
//   flip.capture();                    // just BEFORE a state change that re-orders, inserts or duplicates rows
//   flip.exit(key, () => remove(key))  // plays the row's leaving animation, then calls remove
//
// How it works. capture() records where every row is, measured from the TOP OF THE LIST (so scrolling the page between two
// measurements changes nothing). After React has put the rows in their new places - and before the browser paints - the
// layout effect measures again and plays each row from where it WAS to where it is now: it slides. A row that was not
// there before fades and eases in. Capturing only on request (not on every render) keeps typing in a form free of
// measuring, and a layout change that is not a move (an error line appearing, a row growing) is not animated.
//
// Motion is never required: with `prefers-reduced-motion: reduce`, or a browser without element.animate(), every call
// degrades to the plain instant change - the data never waits on an animation.

export const prefersReducedMotion = () => typeof window !== 'undefined' && typeof window.matchMedia === 'function'
  && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const EASE_OUT = 'cubic-bezier(0.22, 0.9, 0.3, 1)';
const EASE_IN_OUT = 'cubic-bezier(0.4, 0, 0.2, 1)';

// Our animations carry ids so they can be told apart from anything else running on the same element (a CSS transition).
const tag = (animation, id) => { try { animation.id = id; } catch (_) { /* a read-only id is no reason to stop */ } return animation; };
const isOurs = (animation) => typeof animation.id === 'string' && animation.id.startsWith('flip') && animation.id !== 'flip-exit';

export default function useFlipList(signature, { selector = '[data-flip-key]', attr = 'flipKey', liftKey = null } = {}) {
  const containerRef = useRef(null);
  const firstRef = useRef(null);
  const liftRef = useRef(liftKey);
  liftRef.current = liftKey;
  const optionsRef = useRef({ selector, attr });
  optionsRef.current = { selector, attr };

  // key -> top of the row, measured from the top of the list
  const measure = useCallback(() => {
    const root = containerRef.current;
    if (!root) return null;
    const { selector: rowSelector, attr: keyAttr } = optionsRef.current;
    const rootTop = root.getBoundingClientRect().top;
    const positions = new Map();
    root.querySelectorAll(rowSelector).forEach((row) => {
      const key = row.dataset[keyAttr];
      if (key != null) positions.set(key, row.getBoundingClientRect().top - rootTop);
    });
    return positions;
  }, []);

  const capture = useCallback(() => {
    if (prefersReducedMotion()) { firstRef.current = null; return; }
    const positions = measure();
    firstRef.current = positions ? { at: Date.now(), positions } : null;
  }, [measure]);

  useLayoutEffect(() => {
    const first = firstRef.current;
    firstRef.current = null;
    const root = containerRef.current;
    // a capture that no change followed (older than 2 s) must not animate a later, unrelated change
    if (!first || !root || prefersReducedMotion() || Date.now() - first.at > 2000) return;
    const { selector: rowSelector, attr: keyAttr } = optionsRef.current;
    const rows = [...root.querySelectorAll(rowSelector)];
    // The row's LAYOUT place is wanted, not where an earlier move still has it part-way: stop those first. (The
    // capture above already took the place the eye last saw, mid-flight or not, so the new move carries on from it.)
    rows.forEach((row) => { if (row.getAnimations) row.getAnimations().filter(isOurs).forEach((a) => a.cancel()); });
    const last = measure();
    if (!last) return;
    rows.forEach((row) => {
      if (typeof row.animate !== 'function') return;
      const key = row.dataset[keyAttr];
      const before = first.positions.get(key);
      const after = last.get(key);
      if (before === undefined) {
        tag(row.animate(
          [{ opacity: 0, transform: 'translateY(-10px) scale(0.985)' }, { opacity: 1, transform: 'none' }],
          { duration: 300, easing: EASE_OUT },
        ), 'flip-enter');
        return;
      }
      const delta = before - after;
      if (Math.abs(delta) < 1) return;
      // the row that was asked to move draws above the ones it passes
      const z = key === liftRef.current ? 3 : 1;
      tag(row.animate(
        [{ transform: `translateY(${delta}px)`, zIndex: z }, { transform: 'none', zIndex: z }],
        { duration: Math.round(Math.min(560, 240 + Math.abs(delta) * 0.12)), easing: EASE_OUT },
      ), 'flip');
    });
  }, [signature, measure]);

  // The row folds away (its height, its place in the gap and its opacity) and only THEN is `done` called to take it out of
  // the list - so the rows below close up with it instead of jumping afterwards. `done` is called exactly once,
  // whatever happens: a tab in the background does not run animations, and the row must still go.
  const exit = useCallback((key, done, { duration = 220 } = {}) => {
    const root = containerRef.current;
    const { selector: rowSelector, attr: keyAttr } = optionsRef.current;
    const row = root && [...root.querySelectorAll(rowSelector)].find((r) => r.dataset[keyAttr] === key);
    if (!row || typeof row.animate !== 'function' || prefersReducedMotion()) { done(); return; }
    let finished = false;
    const finish = () => { if (finished) return; finished = true; done(); };
    const height = row.getBoundingClientRect().height;
    const gap = parseFloat(window.getComputedStyle(root).rowGap) || 0;
    const animation = tag(row.animate([
      { boxSizing: 'border-box', height: `${height}px`, opacity: 1, transform: 'scale(1)', marginBottom: '0px' },
      { boxSizing: 'border-box', height: '0px', opacity: 0, transform: 'scale(0.97)', marginBottom: `${-gap}px` },
    ], { duration, easing: EASE_IN_OUT, fill: 'forwards' }), 'flip-exit');
    animation.onfinish = finish;
    setTimeout(finish, duration + 160);
  }, []);

  return { containerRef, capture, exit };
}
